import { DOCUMENT, inject, Injectable, SecurityContext } from '@angular/core';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { lang } from 'interfaces';
import { FALLBACK_LANGUAGE } from './browser.token';
import { blobUrlToFile } from './helpers/blob-url-to-file';

@Injectable({
  providedIn: 'root',
})
export class BrowserService {
  protected document = inject<Document>(DOCUMENT);
  protected fallbackLang = inject<lang>(FALLBACK_LANGUAGE);
  private window: Window;
  private sanitizer = inject(DomSanitizer);

  constructor() {
    this.window = this.document.defaultView;
  }

  public getDefaultLanguage() {
    return this.getLanguageFromLocale(navigator?.language);
  }

  protected getLanguageFromLocale(locale: string) {
    return locale?.split('-')?.[0]?.toLowerCase() || this.fallbackLang;
  }

  /**
   * Returns the Location object's URL's origin (protocol + hostname + port).
   * @returns
   */
  public getOrigin() {
    return this.document.location.origin;
  }

  /**
   * Returns the Location object's URL's host and port (if different from the default port for the scheme).
   * @returns
   */
  public getHost() {
    return this.document.location.host;
  }

  public matchMedia(mediaQueryString: string) {
    if (this.window) {
      return this.window.matchMedia(mediaQueryString).matches;
    }

    return false;
  }

  public redirect(url: string): void {
    this.window.location.href = url;
  }

  public openUrlInNewTab(url: string, windowName?: string) {
    this.window.open(url, windowName ?? '_blank');
  }

  public reload(): void {
    this.window.location.reload();
  }

  public getPathWithoutLanguage = () => {
    return this.document.location.pathname.substring(3) || '/';
  };

  public canShare(shareData: ShareData): boolean {
    return !!this.window.navigator?.canShare && this.window.navigator.canShare(shareData);
  }

  public async shareFile(safeUrl: SafeUrl, fileName: string): Promise<void> {
    const url = this.sanitizer.sanitize(SecurityContext.URL, safeUrl);

    if (!url) {
      return;
    }

    try {
      const file = await blobUrlToFile(url, fileName);

      if (this.canShare({ files: [file] })) {
        await navigator.share({
          files: [file],
        });
        return;
      }
    } catch (error: any) {
      if (error.name !== 'AbortError') {
        this.downloadFromUrl(url);
      }
      return;
    }
    this.downloadFromUrl(url);
  }

  private downloadFromUrl(url: string, fileName: string = 'download'): void {
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }
}
